import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { getRankingDashboard } from "../../api/clientVendorDirectoryRankingApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import ClientVendorDirectoryRankingSubnav from "../../components/casamap/admin/ClientVendorDirectoryRankingSubnav";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { formatClientDate } from "../../utils/clientPortalFormat";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.ranking";

function MetricCard({ label, value, hint }) {
  return (
    <div className="cvd-metric-card">
      <div className="cvd-metric-card__label">{label}</div>
      <div className="cvd-metric-card__value">{value}</div>
      {hint ? <div className="cvd-metric-card__hint">{hint}</div> : null}
    </div>
  );
}

export default function ClientVendorDirectoryRankingDashboardPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const base = `${serviciosBase}/posicionamiento`;
  const viewName = activeView?.name || t(`${COPY_PREFIX}.thisView`);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getRankingDashboard(clientViewId);
      setDashboard(body.dashboard);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.dashboard.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="page-content cvd-admin">
      <ClientVendorDirectorySubnav clientViewId={clientViewId} />
      <ClientVendorDirectoryRankingSubnav clientViewId={clientViewId} />
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.dashboard.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.dashboard.intro`, { viewName })}</p>
        </div>
        <div className="cvd-risk-actions">
          <Link className="btn" to={`${serviciosBase}/configuracion`}>
            {t(`${COPY_PREFIX}.dashboard.settings`)}
          </Link>
          <Link className="btn btn-primary" to={`${base}/modelos`}>
            {t(`${COPY_PREFIX}.dashboard.viewModels`)}
          </Link>
        </div>
      </div>

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.dashboard.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}

      {!loading && !errorMessage && dashboard ? (
        <>
          {dashboard.alerts?.length ? (
            <div className="cvd-alert cvd-alert--warning" role="status">
              {t(`${COPY_PREFIX}.dashboard.alerts`, { alerts: dashboard.alerts.join(", ") })}
            </div>
          ) : null}
          <div className="cvd-metric-grid">
            <MetricCard
              label={t(`${COPY_PREFIX}.dashboard.activeModel`)}
              value={dashboard.activeModel ? `v${dashboard.activeModel.version}` : "—"}
              hint={dashboard.activeModel?.name}
            />
            <MetricCard
              label={t(`${COPY_PREFIX}.dashboard.publishedVendors`)}
              value={dashboard.publishedVendors}
            />
            <MetricCard
              label={t(`${COPY_PREFIX}.dashboard.likelyEligible`)}
              value={dashboard.likelyEligibleVendors}
            />
            <MetricCard label={t(`${COPY_PREFIX}.dashboard.topVendor`)} value={dashboard.topVendorCount} />
            <MetricCard
              label={t(`${COPY_PREFIX}.dashboard.lowConfidence`)}
              value={dashboard.lowConfidenceCount}
            />
            <MetricCard
              label={t(`${COPY_PREFIX}.dashboard.activeOverrides`)}
              value={dashboard.activeOverrides}
            />
            <MetricCard
              label={t(`${COPY_PREFIX}.dashboard.activePlacements`)}
              value={dashboard.activePlacements}
            />
            <MetricCard label={t(`${COPY_PREFIX}.dashboard.draftModels`)} value={dashboard.draftModels} />
          </div>
          <div className="cvd-panel">
            <h2>{t(`${COPY_PREFIX}.dashboard.lastRun`)}</h2>
            <p className="cm-muted">
              {t(`${COPY_PREFIX}.dashboard.lastSuccess`, {
                date: dashboard.lastSuccessfulRunAt
                  ? formatClientDate(dashboard.lastSuccessfulRunAt, locale)
                  : "—",
              })}
              {" · "}
              {t(`${COPY_PREFIX}.dashboard.lastFailure`, {
                date: dashboard.lastFailedRunAt ? formatClientDate(dashboard.lastFailedRunAt, locale) : "—",
              })}
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}
